'use client';

import { Stack, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';

import { Animate } from '@/components/Animate';

import loadingAnimation from '../../animations/loading.json';

import classNames from './LoadingState.module.css';

interface LoadingStateProps {
  /**
   * Optional message to display below the loader
   * @default "Loading..."
   */
  message?: string;

  /**
   * Additional message that changes periodically
   * If not provided, no additional messages will be shown
   */
  engagingMessages?: string[];

  /**
   * Error message to display when an error occurs
   */
  errorMessage?: string;
}

/**
 * Standardized loading state component with animated wind effect
 *
 * Used for consistent, visually appealing loading states across the application
 * for page transitions, data fetching, etc.
 */
const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  engagingMessages,
  errorMessage,
}) => {
  // Rotate through engaging messages if provided
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    if (!engagingMessages || engagingMessages?.length <= 1) return;

    const intervalId = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % engagingMessages?.length);
    }, 4500);

    return () => clearInterval(intervalId);
  }, [engagingMessages]);

  return (
    <div className={classNames.loaderContainer}>
      <Stack
        gap="md"
        align="center"
      >
        <Animate
          animationData={loadingAnimation}
          loop={true}
          width="200px"
          height="200px"
          onComplete={() => {}}
        />
        <Title order={3}>{message}</Title>
        {engagingMessages && engagingMessages?.length > 0 && (
          <Text>{engagingMessages[messageIndex]}</Text>
        )}
        {errorMessage && <Text size="md">{errorMessage}</Text>}
      </Stack>
    </div>
  );
};

export { LoadingState };
